import { create } from 'zustand';
import { API_BASE } from './apiBase';

export interface ContractorProfile {
  id: string;
  business_name: string;
  owner_name: string;
  email: string;
  phone: string | null;
  trade: string;
  tier: string;
  status: string;
  stripe_customer_id?: string | null;
}

export interface ContractorAuthResponse {
  access_token: string;
  token_type: 'Bearer';
  expires_in: number;
  contractor: ContractorProfile;
}

interface ContractorAuthState {
  contractor: ContractorProfile | null;
  accessToken: string | null;
  initialized: boolean;
  setAuth: (contractor: ContractorProfile, token: string, expiresIn: number) => void;
  logout: () => void;
  setInitialized: () => void;
}

let _refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleRefresh(expiresIn: number, store: { setAuth: ContractorAuthState['setAuth']; logout: ContractorAuthState['logout'] }) {
  if (_refreshTimer) clearTimeout(_refreshTimer);
  const delay = Math.max((expiresIn - 60) * 1000, 30_000);
  _refreshTimer = setTimeout(async () => {
    try {
      const res = await fetch(`${API_BASE}/contractor/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) { store.logout(); return; }
      const data = await res.json() as ContractorAuthResponse;
      store.setAuth(data.contractor, data.access_token, data.expires_in);
    } catch {
      store.logout();
    }
  }, delay);
}

export const useContractorAuthStore = create<ContractorAuthState>()((set, get) => ({
  contractor: null,
  accessToken: null,
  initialized: false,

  setAuth: (contractor, token, expiresIn) => {
    set({ contractor, accessToken: token });
    try { localStorage.setItem('contractor_session', '1'); } catch { /* private browsing */ }
    scheduleRefresh(expiresIn, get());
  },

  logout: () => {
    if (_refreshTimer) clearTimeout(_refreshTimer);
    _refreshTimer = null;
    try { localStorage.removeItem('contractor_session'); } catch {}
    set({ contractor: null, accessToken: null });
  },

  setInitialized: () => set({ initialized: true }),
}));
